import { useEffect, useState } from "react";
import { ApiError } from "@plotlounge/shared/api";
import {
  useBookSearch,
  type BookSearchResult,
  type BookWithSegments,
  type CreateRoomInput,
  type CreateRoomSegmentInput,
} from "@/lib/clubs/rooms";
import styles from "./RoomCreationForm.module.scss";

const SECTIONS = [
  { id: "book", label: "Book" },
  { id: "segments", label: "Segments" },
  { id: "review", label: "Review" },
];

const MIN_SEGMENTS = 1;
const MAX_SEGMENTS = 10;

export interface RoomCreationFormProps {
  clubId: string;
  clubName: string;
  /** Book picked as winner of the latest voting session, if any. */
  winnerBook: { id: string; title: string; coverUrl?: string | null } | null;
  /** Detailed book record (segments, page count, external IDs). */
  book: BookWithSegments | null;
  isBookLoading: boolean;
  isSubmitting: boolean;
  submitError: Error | null;
  onSubmit: (input: CreateRoomInput) => void;
  onDiscard: () => void;
}

interface SegmentRow {
  number: number;
  title: string;
  startPercent: number;
  endPercent: number;
}

type Errors = { form?: string; segments?: string };

function makeSegments(count: number, seed?: SegmentRow[]): SegmentRow[] {
  const rows: SegmentRow[] = [];
  for (let i = 1; i <= count; i++) {
    const existing = seed?.find((s) => s.number === i);
    rows.push({
      number: i,
      title: existing?.title || `Part ${i}`,
      startPercent: Math.round(((i - 1) / count) * 100),
      endPercent: Math.round((i / count) * 100),
    });
  }
  return rows;
}

function seedFromBook(book: BookWithSegments | null): SegmentRow[] | undefined {
  if (!book || book.segments.length === 0) return undefined;
  return book.segments.map((s, i) => ({
    number: s.segmentNumber,
    title: s.title ?? `Part ${s.segmentNumber}`,
    startPercent:
      s.startPercent ?? Math.round((i / book.segments.length) * 100),
    endPercent:
      s.endPercent ?? Math.round(((i + 1) / book.segments.length) * 100),
  }));
}

export function RoomCreationForm({
  clubId,
  clubName,
  winnerBook,
  book,
  isBookLoading,
  isSubmitting,
  submitError,
  onSubmit,
  onDiscard,
}: RoomCreationFormProps) {
  const [segmentCount, setSegmentCount] = useState(4);
  const [segments, setSegments] = useState<SegmentRow[]>(() => makeSegments(4));
  const [errors, setErrors] = useState<Errors>({});
  const [pickedBook, setPickedBook] = useState<BookSearchResult | null>(null);
  const [winnerReplaced, setWinnerReplaced] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const useWinner = !winnerReplaced && winnerBook && !pickedBook;
  const activeWinner = useWinner ? winnerBook : null;
  const activeBook = pickedBook;

  const showSearch = !activeWinner && !activeBook;
  const search = useBookSearch(showSearch ? searchQuery : "");

  // Seed segment editor from the book when one loads with segments
  // (only relevant for the voting-winner path).
  useEffect(() => {
    if (!useWinner) return;
    const seeded = seedFromBook(book);
    if (seeded && seeded.length > 0) {
      setSegmentCount(seeded.length);
      setSegments(seeded);
    }
  }, [book, useWinner]);

  const updateSegmentCount = (next: number) => {
    const clamped = Math.max(MIN_SEGMENTS, Math.min(MAX_SEGMENTS, next));
    setSegmentCount(clamped);
    setSegments((prev) => makeSegments(clamped, prev));
  };

  const updateSegmentTitle = (index: number, title: string) => {
    setSegments((prev) =>
      prev.map((s, i) => (i === index ? { ...s, title } : s)),
    );
  };

  const selectBook = (b: BookSearchResult) => {
    setPickedBook(b);
    setSearchQuery("");
    // Manually picked books come from external sources without DB segments —
    // reset the segment editor to the default plan.
    setSegmentCount(4);
    setSegments(makeSegments(4));
  };

  const replaceBook = () => {
    setPickedBook(null);
    setWinnerReplaced(true);
    setSearchQuery("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const built = buildPayload(
      clubId,
      activeBook,
      useWinner ? book : null,
      activeWinner,
      segments,
    );
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    onSubmit(built.payload);
  };

  const bookLabel =
    activeBook?.title ??
    (useWinner ? (book?.title ?? winnerBook?.title) : "") ??
    "—";
  const author =
    activeBook?.author ?? (useWinner ? (book?.author ?? "") : "") ?? "";

  const apiErrorMessage =
    submitError instanceof ApiError
      ? submitError.message
      : (submitError?.message ?? null);

  const hasBook = Boolean(activeBook ?? activeWinner);
  const canSubmit = hasBook && !isSubmitting;

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <div>
          <p className={styles.eyebrow}>{clubName} · New room</p>
          <h1 className={styles.title}>Stand up the next room</h1>
          <p className={styles.sub}>
            Pick the book and how it splits into segments. Members of {clubName}{" "}
            get access as soon as you open the room.
          </p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            onClick={onDiscard}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? "Opening…" : "Open to members"}
          </button>
        </div>
      </header>

      <div className={styles.layout}>
        <aside className={styles.spy}>
          <span className={styles.spyHead}>On this page</span>
          <ul>
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>
                  <span className={styles.spyNum}>0{i + 1}</span>
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <Section
            id="book"
            num="01"
            title="Book"
            description="Prefilled from the latest voting winner when available, or search the catalog."
          >
            {activeWinner ? (
              <div className={`${styles.bookCard} ${styles.bookCardWithCover}`}>
                <span className={styles.bookCover}>
                  {activeWinner.coverUrl ? (
                    <img src={activeWinner.coverUrl} alt="" />
                  ) : (
                    <span>{initials(activeWinner.title)}</span>
                  )}
                </span>
                <div className={styles.bookBody}>
                  <span className={styles.fieldLabel}>From voting session</span>
                  <strong className={styles.bookTitle}>
                    {activeWinner.title}
                  </strong>
                  {book?.author && (
                    <span className={styles.bookMeta}>{book.author}</span>
                  )}
                  {isBookLoading && (
                    <span className={styles.bookHint}>
                      Loading book details…
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={replaceBook}
                >
                  Replace
                </button>
              </div>
            ) : activeBook ? (
              <div className={`${styles.bookCard} ${styles.bookCardWithCover}`}>
                <span className={styles.bookCover}>
                  {activeBook.coverUrl ? (
                    <img src={activeBook.coverUrl} alt="" />
                  ) : (
                    <span>{initials(activeBook.title)}</span>
                  )}
                </span>
                <div className={styles.bookBody}>
                  <span className={styles.fieldLabel}>Selected book</span>
                  <strong className={styles.bookTitle}>
                    {activeBook.title}
                  </strong>
                  <span className={styles.bookMeta}>
                    {activeBook.author}
                    {activeBook.pageCount ? ` · ${activeBook.pageCount}p` : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={replaceBook}
                >
                  Replace
                </button>
              </div>
            ) : (
              <div className={styles.bookPicker}>
                <input
                  type="search"
                  className={styles.input}
                  placeholder="Search by title or author…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                  aria-label="Search for a book"
                />
                {searchQuery.trim().length >= 2 && search.isLoading && (
                  <p className={styles.fieldHint}>Searching…</p>
                )}
                {search.data && search.data.length > 0 && (
                  <div className={styles.bookSearchResults} role="listbox">
                    {search.data.map((b, i) => (
                      <button
                        key={
                          b.openLibraryId ||
                          b.googleBooksId ||
                          b.isbn ||
                          `${b.title}-${b.author}-${i}`
                        }
                        type="button"
                        role="option"
                        aria-selected="false"
                        className={styles.bookSearchRow}
                        onClick={() => selectBook(b)}
                      >
                        {b.coverUrl ? (
                          <img
                            className={styles.bookSearchCover}
                            src={b.coverUrl}
                            alt=""
                          />
                        ) : (
                          <span
                            className={styles.bookSearchCover}
                            aria-hidden
                          />
                        )}
                        <span className={styles.bookSearchMeta}>
                          <span className={styles.bookSearchTitle}>
                            {b.title}
                          </span>
                          <span className={styles.bookSearchAuthor}>
                            {b.author}
                            {b.pageCount ? ` · ${b.pageCount}p` : ""}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery.trim().length >= 2 &&
                  !search.isLoading &&
                  search.data &&
                  search.data.length === 0 && (
                    <p className={styles.fieldHint}>
                      No books found for &ldquo;{searchQuery}&rdquo;.
                    </p>
                  )}
              </div>
            )}
          </Section>

          <Section
            id="segments"
            num="02"
            title="Segments"
            description="Split the book into the parts members will read together."
          >
            <div className={styles.segHead}>
              <span className={styles.fieldLabel}>
                {segmentCount} segment{segmentCount === 1 ? "" : "s"}
              </span>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.stepperBtn}
                  onClick={() => updateSegmentCount(segmentCount - 1)}
                  disabled={segmentCount <= MIN_SEGMENTS}
                  aria-label="Decrease segments"
                >
                  −
                </button>
                <span className={styles.stepperVal}>{segmentCount}</span>
                <button
                  type="button"
                  className={styles.stepperBtn}
                  onClick={() => updateSegmentCount(segmentCount + 1)}
                  disabled={segmentCount >= MAX_SEGMENTS}
                  aria-label="Increase segments"
                >
                  +
                </button>
              </div>
            </div>
            <ol className={styles.segList}>
              {segments.map((s, i) => (
                <li key={s.number} className={styles.segRow}>
                  <span className={styles.segNum}>{s.number}</span>
                  <input
                    className={styles.segInput}
                    value={s.title}
                    onChange={(e) => updateSegmentTitle(i, e.target.value)}
                    placeholder={`Part ${s.number}`}
                    aria-label={`Segment ${s.number} title`}
                  />
                  <span className={styles.segMeta}>
                    {s.startPercent}–{s.endPercent}%
                  </span>
                </li>
              ))}
            </ol>
            {errors.segments && (
              <span className={styles.fieldError}>{errors.segments}</span>
            )}
          </Section>

          <Section
            id="review"
            num="03"
            title="Review"
            description="A quick recap before you open the room."
          >
            <div className={styles.previewPanel}>
              <span className={styles.previewLabel}>Live preview</span>
              <span>
                <strong>{bookLabel}</strong>
                {author ? ` · ${author}` : ""}
              </span>
              <span>
                {segmentCount} segment{segmentCount === 1 ? "" : "s"}
              </span>
              <span>All {clubName} members</span>
            </div>

            <ul className={styles.review}>
              <li>
                <strong>Book</strong>
                <span>
                  {bookLabel}
                  {author ? ` — ${author}` : ""}
                </span>
              </li>
              <li>
                <strong>Segments</strong>
                <span>
                  {segmentCount} segment{segmentCount === 1 ? "" : "s"}
                </span>
              </li>
              <li>
                <strong>Members</strong>
                <span>All {clubName} members</span>
              </li>
            </ul>

            {errors.form && (
              <div className={styles.errorBlock}>{errors.form}</div>
            )}
            {apiErrorMessage && (
              <div className={styles.errorBlock}>{apiErrorMessage}</div>
            )}

            <div className={styles.reviewCta}>
              <button
                type="button"
                className={styles.btn}
                onClick={onDiscard}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={!canSubmit}
              >
                {isSubmitting ? "Opening…" : "Open to members"}
              </button>
            </div>
          </Section>
        </form>
      </div>
    </div>
  );
}

function Section({
  id,
  num,
  title,
  description,
  children,
}: {
  id: string;
  num: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={styles.section}>
      <header className={styles.sectionHead}>
        <span className={styles.sectionNum}>{num}</span>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <p className={styles.sectionDesc}>{description}</p>
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

function initials(title: string): string {
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function buildPayload(
  clubId: string,
  picked: BookSearchResult | null,
  winnerDetail: BookWithSegments | null,
  winnerBook: { id: string; title: string; coverUrl?: string | null } | null,
  segments: SegmentRow[],
): { ok: true; payload: CreateRoomInput } | { ok: false; errors: Errors } {
  if (!picked && !winnerBook) {
    return { ok: false, errors: { form: "Pick a book first." } };
  }
  if (segments.length < 1) {
    return { ok: false, errors: { segments: "Add at least one segment." } };
  }
  for (const s of segments) {
    if (!s.title.trim()) {
      return {
        ok: false,
        errors: { segments: "Every segment needs a title." },
      };
    }
  }

  const segmentPayload: CreateRoomSegmentInput[] = segments.map((s) => ({
    number: s.number,
    title: s.title.trim(),
    startPercent: s.startPercent,
    endPercent: s.endPercent,
  }));

  // Picked-from-search path takes priority; otherwise pull from the
  // voting-winner detail row.
  const source: {
    title: string;
    author: string | null;
    description?: string | null;
    coverUrl?: string | null;
    pageCount?: number | null;
    openLibraryId?: string | null;
    googleBooksId?: string | null;
    isbn?: string | null;
  } = picked
    ? picked
    : (winnerDetail ?? {
        title: winnerBook!.title,
        author: null,
        coverUrl: winnerBook!.coverUrl ?? null,
      });

  return {
    ok: true,
    payload: {
      clubId,
      bookTitle: source.title,
      bookAuthor: source.author ?? "Unknown",
      ...(source.description ? { bookDescription: source.description } : {}),
      ...(source.coverUrl ? { bookCoverUrl: source.coverUrl } : {}),
      ...(source.pageCount ? { bookPageCount: source.pageCount } : {}),
      ...(source.openLibraryId ? { openLibraryId: source.openLibraryId } : {}),
      ...(source.googleBooksId ? { googleBooksId: source.googleBooksId } : {}),
      ...(source.isbn ? { isbn: source.isbn } : {}),
      segments: segmentPayload,
    },
  };
}
